import React, { useEffect, useState } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import GlobalStyles from "styles/GlobalStyles";
import { css } from "styled-components/macro"; //eslint-disable-line
import { api } from "supabase/database/api.ts";
import { buildRouteTree, flattenPaths, renderRoutes, SmartFallbackRoute } from "routes";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

/* Providers and Helpers */
import { AuthProvider } from "hooks/auth";
import { CampaignProvider } from "hooks/useCampaign";
import ProtectedRoute from "helpers/ProtectedRoute";
import ActiveRoute from "helpers/ActiveRoute";

/* Auth Pages */
import LoginPage from "pages/authPages/Login.js";
import RegisterPage from "pages/authPages/Register.js";
import ForgotPasswordPage from "pages/authPages/ForgotPassword.js";
import VerifyEmailPage from "pages/authPages/EmailVerification";
import DisclaimerPage from "pages/Disclaimer";

/* Unique Pages */
import HomeMenu from "components/views/HomeMenu";
import HeavenlyInbox from "pages/HeavenlyInbox";


export default function App() {
	const [routes, setRoutes] = useState([]);
	const [menuList, setMenuList] = useState([]);
	const [validPaths, setValidPaths] = useState([]);

	useEffect(() => {
		async function setAppRoutes() {
			const needsFreshData =
				window.location.search.includes("fresh=true") ||
				sessionStorage.getItem("bypassRouteCache") === "true";

			const data = await api.routeQueries.getRoutes({
				bypassCache: needsFreshData
			});

			const orderedData = buildRouteTree(data);
			const paths = flattenPaths(orderedData);

			setRoutes(orderedData);
			setMenuList(orderedData[0]?.children || []);
			setValidPaths(paths);

			if (needsFreshData) {
				sessionStorage.removeItem("bypassRouteCache");
			}
		}

		setAppRoutes();
	}, []);

	// Used to force fresh reload
	window.refreshRoutes = () => {
		sessionStorage.setItem("bypassRouteCache", "true");
		window.location.reload();
	};

	// Wait until validPaths exist
	if (!validPaths.length) {
		return null;
	}

	return (
		<>
			<GlobalStyles />
			<Router>
				<AuthProvider>
					<CampaignProvider>
						<Routes>
							<Route
								path="/"
								element={
									<ActiveRoute>
										<LoginPage />
									</ActiveRoute>
								}
							/>
							<Route path="/login" element={<LoginPage />} />
							<Route path="/register" element={<RegisterPage />} />
							<Route
								path="/verify-email"
								element={<VerifyEmailPage />}
							/>
							<Route
								path="/forgot-password"
								element={<ForgotPasswordPage />}
							/>
							<Route
								path="/reset-password"
								element={<ForgotPasswordPage mode="reset" />}
							/>
							<Route
								path="/disclaimer"
								element={<DisclaimerPage />}
							/>

							{/* Need to be logged in */}
							<Route
								path="/home"
								element={
									<ProtectedRoute>
										<HomeMenu menu={menuList} />
									</ProtectedRoute>
								}
							/>

							{/* Dynamic database routes */}
							{renderRoutes(routes)}

							<Route
								path="/heavenly-inbox"
								element={
									<ProtectedRoute>
										<HeavenlyInbox />
									</ProtectedRoute>
								}
							/>

							{/* 404 fallback — now VALID */}
							<Route
								path="*"
								element={<SmartFallbackRoute validPaths={validPaths} />}
							/>
						</Routes>
					</CampaignProvider>
				</AuthProvider>
			</Router>
			<ToastContainer
				position="bottom-right"
				autoClose={5000}
				hideProgressBar={false}
				newestOnTop={false}
				closeOnClick
				rtl={false}
				theme="light"
			/>
		</>
	);
}
